import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // ✅ UPDATED
import DashboardLayout from "../components/Layout/DashboardLayout";
import { getUserFromStorage } from "../utils/auth";
import { statusLabels } from "../utils/statusLabels";
import axios from "../api/axios";

import {
  CircularProgress,
  Button,
  Typography,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
 
  Paper,
  Divider,
  Chip,
  Stack,
  IconButton,
  Skeleton,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const fmtMoney = (value, currency = "") => {
  const n = Number(value ?? 0);
  const num = Number.isFinite(n) ? n : 0;
  return `${num.toFixed(2)}${currency ? ` ${currency}` : ""}`;
};

const fmtDateFR = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "En attente",
    partially_paid: "Partiellement payé",
    paid: "Retiré",
    cancelled: "Annulé",
  };
  return labels[status] || statusLabels[status] || status || "—";
};

const getStatusChipProps = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "partially_paid") return { label: "Partiellement payé", color: "info", variant: "filled" };
  if (s === "pending") return { label: "En attente", color: "warning", variant: "filled" };
  if (s === "paid") return { label: "Retiré", color: "success", variant: "filled" };
  if (s === "cancelled") return { label: "Annulé", color: "error", variant: "filled" };
  return { label: getStatusLabel(status), color: "default", variant: "outlined" };
};

const TransactionDetailsPage = () => {
  const { secret_code } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // ✅ NEW

  const [user, setUser] = useState(null);
  const [transaction, setTransaction] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [delegateUsed, setDelegateUsed] = useState(false);
  const [delegateName, setDelegateName] = useState("");
  const [delegatePhone, setDelegatePhone] = useState("");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // ✅ Back preserves the list filters
  const handleBack = useCallback(() => {
    const returnTo = location.state?.returnTo;
    if (typeof returnTo === "string" && returnTo.length > 0) return navigate(returnTo);
    navigate(-1);
  }, [location.state, navigate]);

  useEffect(() => {
    const storedUser = getUserFromStorage();

    if (!storedUser || !["admin", "agent"].includes(storedUser.role)) {
      navigate("/login");
      return;
    }

    setUser(storedUser);

    const fetchTransaction = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await axios.get(`/transactions/${secret_code}`);
        setTransaction(res.data);

        if (res.data?.delegate_name || res.data?.delegate_phone) {
          setDelegateUsed(true);
          setDelegateName(res.data?.delegate_name ?? "");
          setDelegatePhone(res.data?.delegate_phone ?? "");
        }
      } catch (err) {
        console.error("Erreur chargement transaction:", err);
        setError("Impossible de charger la transaction.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [navigate, secret_code]);

  const currency = useMemo(() => transaction?.currency ?? "", [transaction]);
  const statusChip = useMemo(() => getStatusChipProps(transaction?.status), [transaction?.status]);

  const handleConfirmPaid = useCallback(async () => {
    if (delegateUsed && (!delegateName || !delegatePhone)) {
      setSnackbar({ open: true, message: "Nom et téléphone du délégué sont requis", severity: "error" });
      return;
    }

    try {
      setUpdatingStatus(true);

      const payload = { status: "paid" };
      if (delegateUsed) {
        payload.delegate_name = delegateName.trim();
        payload.delegate_phone = delegatePhone.trim();
      }

      await axios.patch(`/transactions/${secret_code}/status`, payload);

      const updatedTransaction = {
        ...transaction,
        status: "paid",
        delegate_name: delegateUsed ? delegateName.trim() : null,
        delegate_phone: delegateUsed ? delegatePhone.trim() : null,
      };

      setSnackbar({ open: true, message: "Transaction confirmée avec succès", severity: "success" });

      // ✅ carry returnTo to receipt too
      const returnTo = location.state?.returnTo;
      navigate(`/transaction/${secret_code}/receipt`, { state: { tx: updatedTransaction, returnTo } });
    } catch (err) {
      console.error("Erreur confirmation retrait:", err);
      const serverMessage = err?.response?.data?.error || "Erreur confirmation retrait.";
      setSnackbar({ open: true, message: serverMessage, severity: "error" });
    } finally {
      setUpdatingStatus(false);
      setConfirmDialogOpen(false);
    }
  }, [delegateName, delegatePhone, delegateUsed, navigate, secret_code, transaction, location.state]);

  const handlePartialPayment = useCallback(async () => {
    const amount = Number(partialAmount);
    const remaining = Number(transaction?.remaining_amount ?? transaction?.amount ?? 0);

    if (!Number.isFinite(amount) || amount <= 0) {
      setSnackbar({ open: true, message: "Saisissez un montant de retrait valide", severity: "error" });
      return;
    }
    if (amount >= remaining) {
      setSnackbar({ open: true, message: "Pour solder la transaction, utilisez le paiement total", severity: "error" });
      return;
    }
    if (delegateUsed && (!delegateName || !delegatePhone)) {
      setSnackbar({ open: true, message: "Nom et téléphone du délégué sont requis", severity: "error" });
      return;
    }

    try {
      setUpdatingStatus(true);
      const payload = {
        status: "partially_paid",
        payment_amount: amount,
        payment_note: paymentNote.trim() || undefined,
      };
      if (delegateUsed) {
        payload.delegate_name = delegateName.trim();
        payload.delegate_phone = delegatePhone.trim();
      }

      await axios.patch(`/transactions/${secret_code}/status`, payload);
      const res = await axios.get(`/transactions/${secret_code}`);
      const updatedTransaction = res.data;
      setTransaction(updatedTransaction);
      setPartialAmount("");
      setPaymentNote("");
      setPartialDialogOpen(false);

      const returnTo = location.state?.returnTo;
      navigate(`/transaction/${secret_code}/receipt`, {
        state: { tx: updatedTransaction, returnTo },
      });
    } catch (err) {
      const serverMessage = err?.response?.data?.error || "Erreur lors du paiement partiel.";
      setSnackbar({ open: true, message: serverMessage, severity: "error" });
    } finally {
      setUpdatingStatus(false);
    }
  }, [partialAmount, paymentNote, transaction, delegateUsed, delegateName, delegatePhone, secret_code, navigate, location.state]);

  const handleCorrectTransaction = useCallback(async () => {
    if (!correctionReason || correctionReason.trim().length < 3) {
      setSnackbar({ open: true, message: "La raison de correction est requise.", severity: "error" });
      return;
    }
    try {
      setUpdatingStatus(true);

      const createPath = user?.role === "admin"
        ? "/admin/transactions/create"
        : "/transactions/create";

      navigate(createPath, {
        state: {
          correction: {
            secret_code: transaction.secret_code,
            sender_name: transaction.sender_name,
            sender_phone: transaction.sender_phone,
            receiver_name: transaction.receiver_name,
            receiver_phone: transaction.receiver_phone,
            amount: transaction.amount,
            currency: transaction.currency,
            transaction_fee_percentage: transaction.transaction_fee_percentage,
            correction_reason: correctionReason.trim(),
            sender_location_id: transaction.sender_location_id,
            receiver_location_id: transaction.receiver_location_id,
          },
        },
      });
    } catch (err) {
      const serverMessage = err?.response?.data?.error || "Impossible de préparer la correction.";
      setSnackbar({ open: true, message: serverMessage, severity: "error" });
    } finally {
      setUpdatingStatus(false);
      setCorrectionDialogOpen(false);
    }
  }, [correctionReason, navigate, secret_code, transaction, user?.role]);

  const handleCancelTransaction = useCallback(async () => {
    if (!cancelReason || cancelReason.trim().length < 3) {
      setSnackbar({ open: true, message: "La raison d'annulation est requise (au moins 3 caractères).", severity: "error" });
      return;
    }

    try {
      setUpdatingStatus(true);

      const payload = { status: "cancelled", cancellation_reason: cancelReason.trim() };
      await axios.patch(`/transactions/${secret_code}/status`, payload);

      setTransaction((prev) => ({
        ...prev,
        status: "cancelled",
        cancellation_reason: cancelReason.trim(),
      }));

      setCancelDialogOpen(false);
      setSnackbar({ open: true, message: "Transaction annulée avec succès", severity: "success" });
    } catch (err) {
      console.error("Erreur annulation transaction:", err);
      const serverMessage = err?.response?.data?.error || "Erreur annulation transaction.";
      setSnackbar({ open: true, message: serverMessage, severity: "error" });
    } finally {
      setUpdatingStatus(false);
    }
  }, [cancelReason, secret_code]);

  if (!user) return null;

  const storedUser2 = getUserFromStorage();

  const withdrawalDate = transaction?.last_status_update || transaction?.updated_at || null;

  return (
    <DashboardLayout role={storedUser2?.role}>
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: "auto" }}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton onClick={handleBack} aria-label="Retour">
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                  Détails de la transaction
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Code: <b>{secret_code}</b>
                </Typography>
              </Box>
            </Stack>

            {!loading && transaction && (
              <Chip
                label={statusChip.label}
                color={statusChip.color}
                variant={statusChip.variant}
                sx={{ fontWeight: 900, borderRadius: 2, px: 0.5 }}
              />
            )}
          </Stack>
        </Paper>

        {loading && (
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Skeleton variant="text" height={34} />
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            </Stack>
          </Paper>
        )}

        {!loading && error && (
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Paper>
        )}

        {!loading && !error && !transaction && (
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Alert severity="info">Aucune donnée trouvée.</Alert>
          </Paper>
        )}

        {!loading && !error && transaction && (
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>Montants</Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{fmtMoney(transaction.amount, currency)}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 800, mt: 0.5 }}>
                  Commission: {fmtMoney(transaction.fee, currency)}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Total: {fmtMoney((Number(transaction.amount) || 0) + (Number(transaction.fee) || 0), currency)}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography><b>Déjà retiré:</b> {fmtMoney(transaction.paid_total || 0, currency)}</Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  <b>Reste à retirer:</b> {fmtMoney(transaction.remaining_amount ?? transaction.amount, currency)}
                </Typography>
              </Paper>

              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>Localisation</Typography>
                <Typography sx={{ mt: 0.5 }}>
                  <b>Provenance:</b> {transaction.sender_country ?? "—"} / {transaction.sender_city ?? "—"}
                </Typography>
                <Typography>
                  <b>Destination:</b> {transaction.receiver_country ?? "—"} / {transaction.receiver_city ?? "—"}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <b>Date de création:</b> {fmtDateFR(transaction.created_at)}
                </Typography>

                {withdrawalDate && transaction.status === "paid" && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b>Date du retrait:</b> {fmtDateFR(withdrawalDate)}
                  </Typography>
                )}

                {withdrawalDate && transaction.status === "cancelled" && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b>Date de l'annulation:</b> {fmtDateFR(withdrawalDate)}
                  </Typography>
                )}
              </Paper>
            </Stack>

            {Array.isArray(transaction.payout_payments) && transaction.payout_payments.length > 0 && (
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                  Historique des retraits
                </Typography>
                <Stack spacing={1.25}>
                  {transaction.payout_payments.map((payment, index) => (
                    <Box key={payment.id || index}>
                      {index > 0 && <Divider sx={{ mb: 1.25 }} />}
                      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.5}>
                        <Typography sx={{ fontWeight: 900 }}>
                          Retrait {index + 1}: {fmtMoney(payment.amount, payment.currency || currency)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDateFR(payment.paid_at)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2">
                        <b>Effectué par:</b> {payment.paid_by_name || "—"}
                      </Typography>
                      {payment.delegate_name && (
                        <Typography variant="body2">
                          <b>Délégué:</b> {payment.delegate_name} {payment.delegate_phone ? `— ${payment.delegate_phone}` : ""}
                        </Typography>
                      )}
                      {payment.note && (
                        <Typography variant="body2"><b>Note:</b> {payment.note}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>Informations</Typography>
                  <Typography sx={{ mt: 0.5 }}><b>Code secret:</b> {transaction.secret_code}</Typography>
                  <Typography><b>Statut:</b> {getStatusLabel(transaction.status)}</Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography><b>Agent créateur:</b> {transaction.created_by_name || "—"}</Typography>
                  {transaction.updated_by_name && (
                    <Typography><b>Dernière mise à jour par:</b> {transaction.updated_by_name}</Typography>
                  )}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>Délégué / Annulation</Typography>

                  {transaction.delegate_name ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography><b>Délégué:</b> {transaction.delegate_name}</Typography>
                      <Typography><b>Téléphone délégué:</b> {transaction.delegate_phone || "—"}</Typography>
                      {transaction.delegate_id_number && (
                        <Typography><b>ID délégué:</b> {transaction.delegate_id_number}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ mt: 0.5, color: "text.secondary" }}>Aucun délégué</Typography>
                  )}

                  {transaction.cancellation_reason && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <b>Raison d’annulation:</b> {transaction.cancellation_reason}
                      </Alert>
                    </>
                  )}
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReceiptLongIcon />
                  <Typography sx={{ fontWeight: 900 }}>Reçu</Typography>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  {["pending", "partially_paid", "paid"].includes(transaction.status) && (
                    <Button
                      variant="outlined"
                      startIcon={<ReceiptLongIcon />}
                      onClick={() => {
                        const returnTo = location.state?.returnTo;
                        navigate(`/transaction/${secret_code}/receipt`, { state: { tx: transaction, returnTo } });
                      }}
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Voir le reçu
                    </Button>
                  )}

                  {["pending", "partially_paid"].includes(transaction.status) && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => setConfirmDialogOpen(true)}
                        disabled={updatingStatus}
                        sx={{ borderRadius: 2, fontWeight: 900 }}
                      >
                        {transaction.status === "partially_paid" ? "Payer le solde" : "Paiement total"}
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={() => setPartialDialogOpen(true)}
                        disabled={updatingStatus}
                        sx={{ borderRadius: 2, fontWeight: 900 }}
                      >
                        Paiement partiel
                      </Button>

                      {transaction.status === "pending" && (
                        <Button
                          variant="outlined"
                          onClick={() => setCorrectionDialogOpen(true)}
                          disabled={updatingStatus}
                          sx={{ borderRadius: 2, fontWeight: 900 }}
                        >
                          Corriger
                        </Button>
                      )}

                      {transaction.status === "pending" && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelOutlinedIcon />}
                          onClick={() => setCancelDialogOpen(true)}
                          disabled={updatingStatus}
                          sx={{ borderRadius: 2, fontWeight: 900 }}
                        >
                          Annuler
                        </Button>
                      )}
                    </>
                  )}
                </Stack>
              </Stack>

            </Paper>
          </Stack>
        )}

        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 900 }}>Confirmer le retrait</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              Voulez-vous confirmer ce retrait ? Si un délégué retire l’argent, cochez l’option et remplissez les infos.
            </Alert>

            <FormControlLabel
              control={<Checkbox checked={delegateUsed} onChange={(e) => setDelegateUsed(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 800 }}>Retrait par délégué</Typography>}
            />

            {delegateUsed && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <TextField label="Nom du délégué" value={delegateName} onChange={(e) => setDelegateName(e.target.value)} fullWidth />
                <TextField label="Téléphone du délégué" value={delegatePhone} onChange={(e) => setDelegatePhone(e.target.value)} fullWidth />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmDialogOpen(false)} sx={{ fontWeight: 800 }}>Fermer</Button>
            <Button variant="contained" onClick={handleConfirmPaid} disabled={updatingStatus} sx={{ fontWeight: 900, borderRadius: 2 }}>
              {updatingStatus ? "Traitement..." : "Confirmer"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={partialDialogOpen} onClose={() => setPartialDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 900 }}>Paiement partiel</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              Reste à retirer : <b>{fmtMoney(transaction?.remaining_amount ?? transaction?.amount, currency)}</b>
            </Alert>
            <TextField
              label={`Montant à remettre (${currency})`}
              type="number"
              value={partialAmount}
              onChange={(e) => setPartialAmount(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Checkbox checked={delegateUsed} onChange={(e) => setDelegateUsed(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 800 }}>Retrait par délégué</Typography>}
            />
            {delegateUsed && (
              <Stack spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                <TextField label="Nom du délégué" value={delegateName} onChange={(e) => setDelegateName(e.target.value)} fullWidth />
                <TextField label="Téléphone du délégué" value={delegatePhone} onChange={(e) => setDelegatePhone(e.target.value)} fullWidth />
              </Stack>
            )}
            <TextField
              label="Note (optionnelle)"
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              fullWidth
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setPartialDialogOpen(false)}>Fermer</Button>
            <Button variant="contained" onClick={handlePartialPayment} disabled={updatingStatus}>
              {updatingStatus ? "Traitement..." : "Enregistrer le paiement"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={correctionDialogOpen} onClose={() => setCorrectionDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 900 }}>Corriger la transaction</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
              L’ancienne transaction sera annulée définitivement. Son reçu et son code secret
              deviendront invalides. Un formulaire prérempli s’ouvrira pour créer une nouvelle
              transaction avec un nouveau code secret.
            </Alert>
            <TextField
              label="Raison de la correction"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCorrectionDialogOpen(false)}>Fermer</Button>
            <Button
              variant="contained"
              onClick={handleCorrectTransaction}
              disabled={updatingStatus || correctionReason.trim().length < 3}
            >
              {updatingStatus ? "Traitement..." : "Préparer la correction"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 900 }}>Annuler la transaction</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
              Cette action est irréversible. Veuillez indiquer la raison d’annulation.
            </Alert>
            <TextField
              label="Raison d'annulation"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              minRows={3}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCancelDialogOpen(false)} sx={{ fontWeight: 800 }}>Fermer</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelTransaction}
              disabled={updatingStatus || !cancelReason || cancelReason.trim().length < 3}
              sx={{ fontWeight: 900, borderRadius: 2 }}
            >
              {updatingStatus ? "Traitement..." : "Confirmer annulation"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default TransactionDetailsPage;
